import React, { useEffect, useState } from 'react';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { colors } from 'theme/colors';
import { StyledPageSubTitle } from 'theme/styles';
import { ERouteLinks } from 'models/route';
import { useLocation, useNavigate } from 'react-router-dom';
import { IRegisteredFirearmDetailsProps } from 'models/registeredFirearm';
import { useTranslations } from 'hooks/useTranslations';
import { Chips } from 'components/atoms/Chips';
import Link from '@mui/material/Link';
import { useLang } from 'models/langContext';
import { formatDate } from 'utils/date';
import { ECustomFieldObjectType, ECustomFieldType } from 'models/customField';
import { ILegalEntityCustomField } from 'components/organisms/LegalEntityDetails';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { isFRTLocal } from 'pages/FRT/helpers';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { renderDataColorByValues } from 'components/molecules/Table/helpers';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { EAlertVariants, IAlert } from 'components/atoms/Alert';
import { EFirearmStateId } from 'models/firearm';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { Asset } from 'utils/assets';
import { EState, statesWithoutMovedTransaction } from 'utils/transactionUtils';
import { usePrivilegesValidation } from 'utils/privilegesValidations';
import ImagesGallery from '../ImagesGallery';
import { EModuleType } from '../ImagesGallery/helper';
import { isPathEqualToRouteName } from 'utils/route';
import ImagesGalleryRadioButton from '../ImagesGalleryRadioButton';
import { EGalleryTypeFirearm } from '../ImagesGalleryRadioButton/helpers';
import { EMarkingTypes } from 'constants/Markings';
import { findInDictionary } from 'helpers/dictionary';
import { useDictionary } from 'models/dictionaryContext';

// fix organization | organisation problem
export const getLegalEntityType = (type: string | undefined | null) => {
  if (type) {
    return type.toLowerCase() === ELegalEntityTypes.individual
      ? ELegalEntityTypes.individual
      : ELegalEntityTypes.organisation;
  }
  return null;
};

const RegisteredFirearmDetails = ({
  firearm,
  pageType = EDetailsPageTypes.view,
  disableActions = false,
  hideDisableButton = false,
  justShowFRT = false,
  changeLocationForItemInGroupCallback,
  disableManageActions,
}: IRegisteredFirearmDetailsProps) => {
  const navigate = useNavigate();
  const { selectedLanguage, defaultLanguage } = useLang();
  const { t } = useTranslations();
  const { getValidTranslationName } = useLang();
  const { shortDateFormat, getProperties } = useGlobalProperty();
  const { hasPermissions } = usePermission();
  const { getCustomField, customFieldsFirearms } = useLegalEntities();
  const { setAlert } = useAlert();
  const { pathname }: any = useLocation();
  const { isAnyLicenceAuthorityValid } = usePrivilegesValidation();
  const [galleryType, setGalleryType] = useState<EGalleryTypeFirearm>(EGalleryTypeFirearm.FRT);
  const viewFirearm = isPathEqualToRouteName(pathname, ERouteLinks.ViewRegisteredFirearm);
  const [showMarkings, setShowMarkings] = useState(false);
  const {
    getMake,
    make,
    getModel,
    model,
    getFirearmAction,
    firearmAction,
    getFirearmType,
    firearmType,
    getCaliber,
    caliber,
    getManufacturer,
    manufacturer,
  } = useDictionary();

  useEffect(() => {
    (async function initDictionaries() {
      getMake();
      getModel();
      getCaliber();
      getFirearmAction();
      getFirearmType();
      getManufacturer();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const licenceAuthorityValid =
    pageType === EDetailsPageTypes.auditLogView ||
    (!!firearm && isAnyLicenceAuthorityValid(firearm.licenceAuthorityIds));

  useEffect(() => {
    (async () => {
      getProperties();
      await getCustomField(ECustomFieldObjectType.FIREARM);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderDataColor = renderDataColorByValues(
    firearm.activityUniqueId,
    firearm?.legalityUniqueId,
    firearm.stateUniqueId,
  ).stateField;

  const renderCustomFieldData = (customField: ILegalEntityCustomField) => {
    if (customField.customFieldType === ECustomFieldType.BOOL) {
      if (customField.value === null) {
        return '-';
      }
      return customField.value
        ? t('customFields.value.yes.label')
        : t('customFields.value.no.label');
    }
    if (customField.customFieldType === ECustomFieldType.DATE) {
      return formatDate(customField.value, shortDateFormat);
    }
    return customField.value || '-';
  };

  const statesWithoutCoordinates = [
    EFirearmStateId.DESTROYED,
    EFirearmStateId.DISABLED,
    EFirearmStateId.EXPORTED,
  ];

  const handleRedirectToEditPageButton = (): (() => void) => {
    if (customFieldsFirearms?.length && firearm.stateUniqueId !== EFirearmStateId.DISABLED) {
      return () =>
        navigate(`${ERouteLinks.EditRegisteredFirearm.replace(':id', firearm.uniqueId)}`, {
          state: {
            prevPage: `${ERouteLinks.ViewRegisteredFirearm.replace(':id', firearm.uniqueId)}`,
          },
        });
    }
    if (firearm.stateUniqueId === EFirearmStateId.DISABLED) {
      return () =>
        setAlert(ActionTypes.SET_FIREARM_ALERT, {
          text: t('firearm.disabled.edit.info'),
          variant: EAlertVariants.error,
        } as IAlert);
    }
    return () =>
      setAlert(ActionTypes.SET_FIREARM_ALERT, {
        text: t('general.noAvaibleDataToEdit.label'),
        variant: EAlertVariants.warning,
      } as IAlert);
  };

  const handleRedirectToMovedTransaction = () => {
    if (statesWithoutMovedTransaction.includes(firearm.stateUniqueId as EState)) return;

    if (firearm?.groupName) {
      if (changeLocationForItemInGroupCallback) {
        changeLocationForItemInGroupCallback();
      }
      return;
    }

    navigate(ERouteLinks.MoveTransaction, {
      state: {
        assetUniqueId: firearm.uniqueId,
        asset: Asset.FIREARM,
        isGroup: false,
        searchAddressesFor: firearm.keeperId || firearm.ownerId,
        navigateBack: `${ERouteLinks.ViewRegisteredFirearm.replace(
          ':id',
          firearm.uniqueId as string,
        )}`,
        breadcrumbs: [
          {
            label: t('firearms.registeredFirearms.accordion.name'),
            route: ERouteLinks.Firearms,
          },
          {
            label: t('registeredFirearms.firearmsDetails.header'),
            route: `${ERouteLinks.ViewRegisteredFirearm.replace(
              ':id',
              firearm.uniqueId as string,
            )}?expand=firearms`,
          },
          t('transactions.movedTransaction.breadcrumb.label'),
        ],
      },
    });
  };

  const title = firearm.make
    ? `${firearm.make}, ${firearm.model}, ${firearm.action}, ${firearm.type}, ${firearm.caliber}`
    : `${findInDictionary(firearm.makeUniqueId, make)}, ${findInDictionary(firearm.modelUniqueId, model)}, ${findInDictionary(firearm.actionUniqueId, firearmAction)}, ${findInDictionary(firearm.typeUniqueId, firearmType)}, ${findInDictionary(firearm.caliberUniqueId, caliber)}`;

  return (
    <div>
      {firearm && (
        <DetailsTemplate
          disableOnlyDelete
          showOnMapButtonId="firearm-details-show-on-map"
          title={title}
          pageType={pageType}
          redirectToEditView={
            !customFieldsFirearms?.length ||
            disableActions ||
            disableManageActions ||
            !hasPermissions([EPermission.MODIFY_FIREARM])
              ? undefined
              : handleRedirectToEditPageButton()
          }
          redirectToDisableView={
            disableActions ||
            disableManageActions ||
            hideDisableButton ||
            !hasPermissions(EPermission.DISABLE_FIREARM)
              ? undefined
              : () =>
                  navigate(
                    `${ERouteLinks.DisableRegisteredFirearm.replace(':id', firearm.uniqueId)}`,
                  )
          }
          redirectToView={() =>
            navigate(`${ERouteLinks.ViewRegisteredFirearm.replace(':id', firearm.uniqueId)}`)
          }
          redirectToShowOnMap={
            !!firearm.latitude &&
            !!firearm.longitude &&
            !statesWithoutCoordinates.includes(firearm.stateUniqueId as EFirearmStateId)
              ? () =>
                  navigate(ERouteLinks.RegisteredFirearmsMap, {
                    state: {
                      firearmUniqueId: firearm.uniqueId,
                      showAdvanced: true,
                      showLegalityBar: false,
                      navigateBack: `${ERouteLinks.ViewRegisteredFirearm.replace(
                        ':id',
                        firearm.uniqueId as string,
                      )}`,
                      breadcrumbs: [
                        {
                          label: t('firearms.registeredFirearms.accordion.name'),
                          route: ERouteLinks.Firearms,
                        },
                        {
                          label: t('registeredFirearms.firearmsDetails.header'),
                          route: `${ERouteLinks.ViewRegisteredFirearm.replace(
                            ':id',
                            firearm.uniqueId as string,
                          )}?expand=firearms`,
                        },
                        t('registeredFirearms.firearmsLocation.showOnMap.label'),
                      ],
                    },
                  })
              : undefined
          }
          redirectToMovedTransaction={handleRedirectToMovedTransaction}
          hideMovedTransaction={
            (statesWithoutMovedTransaction.includes(firearm.stateUniqueId as EState) &&
              !!changeLocationForItemInGroupCallback) ||
            !licenceAuthorityValid ||
            !hasPermissions(EPermission.ADD_TRANSACTION)
          }
          disableActions={disableActions || disableManageActions}
        >
          <Grid
            container
            size={{ xs: 12 }}
            sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' }, justifyContent: 'center' }}
            columnSpacing="20px"
          >
            {viewFirearm && (
              <Grid container sx={{ gap: '12px', alignContent: 'flex-start', width: '270px' }}>
                <ImagesGalleryRadioButton
                  setGalleryType={setGalleryType}
                  galleryType={galleryType}
                  moduleType={EModuleType.FIREARM}
                />

                <ImagesGallery
                  key={'firearms_details_gallery'}
                  moduleType={EModuleType.FIREARM}
                  ownerId={firearm.uniqueId}
                  buttonId={'firearms_details-add_photo'}
                  buttonPermission={EPermission.MODIFY_FIREARM}
                  showButton={!disableManageActions && !disableActions}
                  hidden={galleryType !== EGalleryTypeFirearm.Owner}
                  init={() => setGalleryType(EGalleryTypeFirearm.Owner)}
                  onClickEdit={() =>
                    navigate(
                      `${ERouteLinks.FirearmEditImages.replace(
                        ':id',
                        firearm.uniqueId as string,
                      )}`,
                    )
                  }
                  onClickAdd={() =>
                    navigate(
                      `${ERouteLinks.FirearmAddImages.replace(
                        ':id',
                        firearm.uniqueId as string,
                      )}`,
                    )
                  }
                />

                <ImagesGallery
                  key={'firearms_details_FRT_gallery'}
                  moduleType={EModuleType.FRT}
                  ownerId={firearm.frtUniqueId}
                  buttonId={'firearms_details_FRT-add_photo'}
                  showButton={false}
                  hidden={galleryType !== EGalleryTypeFirearm.FRT}
                />
              </Grid>
            )}
            <Grid
              container
              size={{ xs: 12, md: 'auto' }}
              sx={{
                width: { xs: '100%', md: viewFirearm ? 'calc(100% - 270px)' : '100%' },
                rowGap: 1,
                alignContent: 'flex-start',
              }}
            >
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('registeredFirearms.firearmsDetails.manufacturer.label')}
                  </Typography>
                  <Typography variant="body2">
                    {firearm.manufacturer
                      ? firearm.manufacturer
                      : findInDictionary(firearm.manufacturerUniqueId, manufacturer)}
                  </Typography>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {isFRTLocal(firearm.frtUniqueId)
                      ? t('registeredFirearms.firearmsDetails.firearmLocalReferenceNo.label')
                      : t('registeredFirearms.firearmsDetails.firearmReferenceNo.label')}
                  </Typography>
                  <Typography variant="body2">
                    {emptyFieldHolder(
                      isFRTLocal(firearm.frtUniqueId) ? firearm.frtId : firearm.frtUniqueId,
                    )}
                  </Typography>
                </Stack>
              </Grid>
              {(!disableActions || justShowFRT) &&
                hasPermissions([EPermission.VIEW_FIREARMS]) &&
                pageType !== EDetailsPageTypes.auditLogView && (
                  <Grid sx={{ alignSelf: 'center' }} size={{ xs: 12, md: 12, lg: 6 }}>
                    <Link
                      href={ERouteLinks.FRTDetails.replace(':frtId', firearm.frtUniqueId)}
                      underline="none"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        fullWidth
                        label={t(
                          'registeredFirearms.firearmsDetails.showFRTInformation.button',
                        )}
                        variant={EButtonVariants.contained}
                        size={EButtonSizes.small}
                      />
                    </Link>
                  </Grid>
                )}
              <Grid container size={{ xs: 12 }}>
                <StyledPageSubTitle
                  variant="subtitle1"
                  sx={{
                    pt: 2.5,
                    width: '100%',
                    borderTop: {
                      xs: `1px solid ${colors.primary25}`,
                    },
                    textTransform: 'uppercase',
                  }}
                >
                  {t('registeredFirearms.firearmsDetails.detailedInformation.section.name')}
                </StyledPageSubTitle>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(
                    'registeredFirearms.firearmsDetails.detailedInformation.section.firecycleID.label',
                  )}
                </Typography>
                <Typography variant="body2">
                  {emptyFieldHolder(firearm.fireCycleId)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(
                    'registeredFirearms.firearmsDetails.detailedInformation.section.serialNumber.label',
                  )}
                </Typography>
                <Typography variant="body2">
                  {emptyFieldHolder(firearm.serialNumber)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(
                    'registeredFirearms.firearmsDetails.detailedInformation.section.exhibit.label',
                  )}
                </Typography>
                <Typography variant="body2">
                  {emptyFieldHolder(firearm.exhibitNumber)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(
                    'registeredFirearms.firearmsDetails.detailedInformation.section.state.label',
                  )}
                </Typography>
                <Chips
                  text={firearm.state}
                  variant={renderDataColor}
                  textId="firearm-state-chip"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(
                    'registeredFirearms.firearmsDetails.detailedInformation.section.currentOwner.label',
                  )}
                </Typography>
                <Typography variant="body2">
                  {pageType !== EDetailsPageTypes.auditLogView ||
                  (pageType === EDetailsPageTypes.auditLogView &&
                    firearm.isOwnerHyperLinkActive) ? (
                    <StyledLegalLink
                      legalId={firearm.ownerId}
                      legalType={getLegalEntityType(firearm.ownerType)}
                      labelText={firearm.owner}
                      legalVisibilityUniqueId={firearm.ownerVisibilityUniqueId}
                    />
                  ) : (
                    <Typography variant="body2">{emptyFieldHolder(firearm.owner)}</Typography>
                  )}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(
                    'registeredFirearms.firearmsDetails.detailedInformation.section.currentKeeper.label',
                  )}
                </Typography>
                <Typography variant="body2">
                  {pageType !== EDetailsPageTypes.auditLogView ||
                  (pageType === EDetailsPageTypes.auditLogView &&
                    firearm.isKeeperHyperLinkActive) ? (
                    <StyledLegalLink
                      legalId={firearm.keeperId}
                      legalType={getLegalEntityType(firearm.keeperType)}
                      labelText={firearm.keeper}
                      legalVisibilityUniqueId={firearm.keeperVisibilityUniqueId}
                    />
                  ) : (
                    <Typography variant="body2">{emptyFieldHolder(firearm.keeper)}</Typography>
                  )}
                </Typography>
              </Grid>
              {firearm.manufacturedAt && (
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(
                      'registeredFirearms.firearmsDetails.detailedInformation.section.manufactureDate.label',
                    )}
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(firearm.manufacturedAt, shortDateFormat)}
                  </Typography>
                </Grid>
              )}
              {firearm.countryOfImport && firearm.countryOfImport.translations && (
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(
                      'registeredFirearms.firearmsDetails.detailedInformation.section.countryOfImport.label',
                    )}
                  </Typography>
                  <Typography variant="body2">
                    {getValidTranslationName(firearm.countryOfImport.translations)}
                  </Typography>
                </Grid>
              )}
              {firearm.countryOfExport && firearm.countryOfExport.translations && (
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(
                      'registeredFirearms.firearmsDetails.detailedInformation.section.countryOfExport.label',
                    )}
                  </Typography>
                  <Typography variant="body2">
                    {getValidTranslationName(firearm.countryOfExport.translations)}
                  </Typography>
                </Grid>
              )}
              {firearm?.customFields?.map((customField) => (
                <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={customField.customFieldUniqueId}>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {customField?.translations?.filter(
                      (translation) =>
                        selectedLanguage?.uniqueId === translation.languageUniqueId,
                    )?.[0]?.name ||
                      customField?.translations?.filter(
                        (translation) =>
                          defaultLanguage?.uniqueId === translation.languageUniqueId,
                      )?.[0]?.name}
                  </Typography>
                  <Typography variant="body2">{renderCustomFieldData(customField)}</Typography>
                </Grid>
              ))}
              {firearm.markings && showMarkings && (
                <>
                  <Grid container size={{ xs: 12 }}>
                    <StyledPageSubTitle
                      variant="subtitle1"
                      sx={{
                        pt: 2.5,
                        width: '100%',
                        borderTop: {
                          xs: `1px solid ${colors.primary25}`,
                        },
                        textTransform: 'uppercase',
                      }}
                    >
                      {t('registeredFirearms.firearmsDetails.firearmMarkings.section.name')}
                    </StyledPageSubTitle>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(
                        'registeredFirearms.firearmsDetails.firearmMarkings.importMarking.label',
                      )}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(
                        firearm.markings.find(
                          (marking) => marking.markingType === EMarkingTypes.IMPORT,
                        )?.marking,
                      )}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(
                        'registeredFirearms.firearmsDetails.firearmMarkings.appliedMarking.label',
                      )}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(
                        firearm.markings.find(
                          (marking) => marking.markingType === EMarkingTypes.APPLIED,
                        )?.marking,
                      )}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(
                        'registeredFirearms.firearmsDetails.firearmMarkings.deactivationMarking.label',
                      )}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(
                        firearm.markings.find(
                          (marking) => marking.markingType === EMarkingTypes.DEACTIVATION,
                        )?.marking,
                      )}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(
                        'registeredFirearms.firearmsDetails.firearmMarkings.exportMarking.label',
                      )}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(
                        firearm.markings.find(
                          (marking) => marking.markingType === EMarkingTypes.EXPORT,
                        )?.marking,
                      )}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(
                        'registeredFirearms.firearmsDetails.firearmMarkings.surplusSaleMarking.label',
                      )}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(
                        firearm.markings.find(
                          (marking) => marking.markingType === EMarkingTypes.SURPLUS_SALE,
                        )?.marking,
                      )}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(
                        'registeredFirearms.firearmsDetails.firearmMarkings.yearOfTransfer.label',
                      )}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(firearm.yearOfTransfer)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(
                        'registeredFirearms.firearmsDetails.firearmMarkings.confiscationMarking.label',
                      )}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(
                        firearm.markings.find(
                          (marking) => marking.markingType === EMarkingTypes.CONFISCATION,
                        )?.marking,
                      )}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(
                        'registeredFirearms.firearmsDetails.firearmMarkings.yearOfConfiscation.label',
                      )}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(firearm.yearOfConfiscation)}
                    </Typography>
                  </Grid>
                </>
              )}
              {firearm.markings && (
                <Grid container size={{ xs: 12 }} sx={{ justifyContent: 'flex-end', mt: 3 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Button
                      fullWidth
                      label={
                        showMarkings
                          ? t('registeredFirearms.firearmsDetails.hideFirearmMarkings.button')
                          : t('registeredFirearms.firearmsDetails.seeFirearmMarkings.button')
                      }
                      variant={EButtonVariants.outlined}
                      size={EButtonSizes.small}
                      onClick={() => setShowMarkings(!showMarkings)}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </DetailsTemplate>
      )}
    </div>
  );
};

export { RegisteredFirearmDetails };
